/**
 * pvpRank.ts — in-app PvP stat-product ranking under league CP caps.
 *
 * Push 2 (v1.4.1 b6): the user asked for Great / Ultra / Master league rank
 * + percent cards on the detail page without depending on an external API.
 * This file computes the canonical stat-product ranking pattern used by
 * PvP IV tools (PokéGenie, pvpoke, pvpivs.com):
 *
 *   For each candidate level L = 1.0, 1.5, ..., 51.0:
 *     CP_at(L) = floor( atk(L) * sqrt(def(L)) * sqrt(sta(L)) * CPM(L)^2 / 10 )
 *     where:
 *       atk(L) = (baseAtk + ivAtk) * CPM(L)
 *       def(L) = (baseDef + ivDef) * CPM(L)
 *       sta(L) = (baseSta + ivSta) * CPM(L)
 *   stat_product(L) = atk(L) * def(L) * floor(sta(L))
 *   Keep the highest stat_product across all L whose CP_at(L) <= cap.
 *
 * Rank is computed by sorting every IV combination (0..15)^3 = 4096 combos
 * by best stat product; rank = strictly-better combos + 1 (ties share the
 * better rank, as Poké Genie does). Percent is
 * `best_stat_product(this_iv) / best_stat_product_overall * 100`.
 *
 * Caveats (rendered honestly to the user when relevant):
 *   - We do not yet model best evolution / shadow / purified bonuses. The
 *     rank shown is for the species AS SCANNED. A pre-evolved species in
 *     the trainer's box may have a different rank when evolved — the UI
 *     should call this out for non-final-stage species.
 *   - Best Buddy +1 level is also not modelled. The cap path returns the
 *     best regular-level outcome; a Best Buddy boost may move a borderline
 *     candidate above or below the cap.
 *   - Master League has no CP cap. Reported as level-50 stat product
 *     (or level-51 when the trainer-level slider allows it).
 *   - Stardust / candy-XL cost not surfaced.
 *
 * Pure module. No DOM, no I/O.
 */

import { cpmAtLevel, computeCp, type BaseStats } from "./ivSolver";

/** PvP league CP caps in Pokémon GO. */
export const LEAGUE_CAPS = {
  little: 500,
  great: 1500,
  ultra: 2500,
  master: null as number | null,
} as const;

export type LeagueId = keyof typeof LEAGUE_CAPS;

const ALL_LEVELS: number[] = (() => {
  const out: number[] = [];
  // Match the CPM table from ivSolver.ts. Half-levels included.
  for (let i = 2; i <= 102; i++) out.push(i / 2);
  return out;
})();

/** CPM per ALL_LEVELS entry, precomputed once — the league sweep reads it
 * hot enough that even the table lookup indirection shows up. */
const ALL_CPMS: number[] = ALL_LEVELS.map((l) => cpmAtLevel(l));

/**
 * Compute the best (highest stat-product) level for an IV combo under a
 * CP cap. Returns null when even level 1 exceeds the cap (shouldn't
 * happen for caps >= 100 but kept defensive).
 *
 * CP and stat product are both nondecreasing in level for fixed IVs
 * (every stat scales with CPM, which grows with level), so the best
 * level is simply the HIGHEST level whose CP fits the cap — found by
 * binary search rather than walking all ~100 half-levels. This is the
 * inner loop of the 4096-combo league sweep, so the ~6x fewer computeCp
 * calls are what keep a 3,000-record GL/UL sort off the freeze path.
 */
export function bestStatProductForCombo(
  base: BaseStats,
  iv: { atk: number; def: number; sta: number },
  cap: number | null,
  maxLevel: number = 51,
): {
  level: number;
  cp: number;
  hp: number;
  statProduct: number;
} | null {
  const hi = (() => {
    let h = ALL_LEVELS.length - 1;
    while (h >= 0 && ALL_LEVELS[h] > maxLevel) h--;
    return h;
  })();
  if (hi < 0) return null;
  let idx: number;
  if (cap == null) {
    idx = hi;
  } else {
    // CP(L) = max(10, floor(K · CPM(L)²)) with K constant per combo, so the
    // binary search runs on the cheap K·cpm² curve; float association can
    // differ from computeCp by an ulp at a floor boundary, so the exact
    // computeCp fix-up below walks at most one step to restore bit-parity.
    const K =
      ((base.atk + iv.atk) *
        Math.sqrt(base.def + iv.def) *
        Math.sqrt(base.sta + iv.sta)) /
      10;
    const capRaw = cap + 1;
    let lo = 0;
    let h = hi;
    while (lo < h) {
      const mid = (lo + h + 1) >> 1;
      const cpm = ALL_CPMS[mid];
      if (K * cpm * cpm < capRaw) lo = mid;
      else h = mid - 1;
    }
    idx = lo;
    while (idx < hi && computeCp(base, iv, ALL_LEVELS[idx + 1]) <= cap) idx++;
    while (idx >= 0 && computeCp(base, iv, ALL_LEVELS[idx]) > cap) idx--;
    if (idx < 0) return null;
  }
  let lvl = ALL_LEVELS[idx];
  let cpm = cpmAtLevel(lvl);
  let sFloor = Math.floor((base.sta + iv.sta) * cpm);
  if (sFloor === 0 && idx > 0) {
    // Degenerate stamina (Shedinja-class): every level's product is 0, and
    // the old linear walk reported the FIRST level. Keep that behaviour.
    lvl = ALL_LEVELS[0];
    cpm = cpmAtLevel(lvl);
    sFloor = Math.floor((base.sta + iv.sta) * cpm);
  }
  const a = (base.atk + iv.atk) * cpm;
  const d = (base.def + iv.def) * cpm;
  return {
    level: lvl,
    cp: computeCp(base, iv, lvl),
    hp: Math.max(10, sFloor),
    statProduct: a * d * sFloor,
  };
}

// Per-(species base, cap, maxLevel) top-3 best-possible stat products,
// descending. One 4096-combo sweep per SPECIES per league instead of per
// RECORD — a 3,000-record list usually holds a few hundred species, and
// repeat sorts are pure cache hits. Three values, not one, so the list rows
// can also ask "is this combo the species' #1/#2/#3?" (the top-3 badge,
// 2026-08-25 ask) from the same sweep the percent already pays for.
const topSpCache = new Map<string, [number, number, number]>();

function topStatProductsFor(
  base: BaseStats,
  cap: number | null,
  maxLevel: number,
): [number, number, number] {
  const key = `${base.atk}|${base.def}|${base.sta}|${cap ?? "-"}|${maxLevel}`;
  const hit = topSpCache.get(key);
  if (hit !== undefined) return hit;
  const top: [number, number, number] = [0, 0, 0];
  for (let a = 0; a <= 15; a++) {
    for (let d = 0; d <= 15; d++) {
      for (let s = 0; s <= 15; s++) {
        const best = bestStatProductForCombo(base, { atk: a, def: d, sta: s }, cap, maxLevel);
        const sp = best ? best.statProduct : 0;
        if (sp > top[0]) {
          top[2] = top[1];
          top[1] = top[0];
          top[0] = sp;
        } else if (sp > top[1]) {
          top[2] = top[1];
          top[1] = sp;
        } else if (sp > top[2]) {
          top[2] = sp;
        }
      }
    }
  }
  topSpCache.set(key, top);
  return top;
}

function topStatProductFor(
  base: BaseStats,
  cap: number | null,
  maxLevel: number,
): number {
  return topStatProductsFor(base, cap, maxLevel)[0];
}

/**
 * 1, 2 or 3 when this IV combo's best stat product is the species' #1 / #2 /
 * #3 of all 4096 under the league cap — null otherwise. Rank here counts
 * strictly-better combos (ties share the better rank), which matches
 * `rankOneLeague` everywhere it matters for a "top 3" claim. Cheap on the
 * list path: one best-level solve plus the cached species-wide top-3.
 */
export function leagueTopRank(
  league: LeagueId,
  input: SpeciesLeagueRanksInput,
): number | null {
  const cap = LEAGUE_CAPS[league];
  const maxLevel = input.maxLevel ?? 50;
  const myBest = bestStatProductForCombo(input.base, input.iv, cap, maxLevel);
  if (!myBest || myBest.statProduct <= 0) return null;
  const top = topStatProductsFor(input.base, cap, maxLevel);
  if (myBest.statProduct < top[2]) return null;
  let rank = 1;
  for (const sp of top) if (sp > myBest.statProduct) rank++;
  return rank <= 3 ? rank : null;
}

// 2026-08-29: full 1-4096 rank for nickname templates ("Azu ᴳ¹"). A fresh
// rankOneLeague sweep per record is fine on one detail page but ruinous
// across a 3,000-record naming queue, so the species-wide stat products are
// swept once per (species, cap, maxLevel) and kept SORTED (same key
// discipline as topSpCache); each record then costs one best-level solve
// plus a binary search.
const sortedSpCache = new Map<string, number[]>();

/**
 * This IV combo's rank among all 4096 spreads for its species under the
 * league cap: 1 = the best possible spread. Ties share the better rank,
 * matching leagueTopRank and rankOneLeague. Null when no level fits the cap.
 */
export function leagueFullRank(
  league: LeagueId,
  input: SpeciesLeagueRanksInput,
): number | null {
  const cap = LEAGUE_CAPS[league];
  const maxLevel = input.maxLevel ?? 50;
  const myBest = bestStatProductForCombo(input.base, input.iv, cap, maxLevel);
  if (!myBest || myBest.statProduct <= 0) return null;
  const key = `${input.base.atk}|${input.base.def}|${input.base.sta}|${cap ?? "-"}|${maxLevel}`;
  let sorted = sortedSpCache.get(key);
  if (!sorted) {
    sorted = [];
    for (let a = 0; a <= 15; a++) {
      for (let d = 0; d <= 15; d++) {
        for (let s = 0; s <= 15; s++) {
          const best = bestStatProductForCombo(input.base, { atk: a, def: d, sta: s }, cap, maxLevel);
          sorted.push(best ? best.statProduct : 0);
        }
      }
    }
    sorted.sort((x, y) => y - x);
    sortedSpCache.set(key, sorted);
  }
  // First index whose product is <= mine = the count of strictly-better
  // combos; rank is one past that.
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] > myBest.statProduct) lo = mid + 1;
    else hi = mid;
  }
  return lo + 1;
}

/**
 * The percent-of-best a full `rankOneLeague` would report, WITHOUT the
 * rank: one best-level solve for this combo plus the (cached) species-wide
 * top stat product. This is the list page's hot path — `rankOneLeague`
 * stays for the detail page, which also wants the 1-4096 rank.
 */
export function leaguePercent(
  league: LeagueId,
  input: SpeciesLeagueRanksInput,
): number | null {
  const cap = LEAGUE_CAPS[league];
  const maxLevel = input.maxLevel ?? 50;
  const myBest = bestStatProductForCombo(input.base, input.iv, cap, maxLevel);
  if (!myBest) return null;
  const top = topStatProductFor(input.base, cap, maxLevel);
  return top > 0 ? (myBest.statProduct / top) * 100 : null;
}

export interface LeagueRanking {
  league: LeagueId;
  /**
   * 1 = best possible IV combo for this species under this cap.
   * 4096 = worst. Null when no combo fits the cap (shouldn't happen at
   * 500/1500/2500 for any standard species).
   */
  rank: number | null;
  /**
   * stat_product(this_iv) / stat_product(overall_best) * 100. 100% = the
   * best IV combo for this league. Null when rank is null.
   */
  percent: number | null;
  /** Best level at the cap for THIS IV combo. */
  level: number | null;
  /** CP that combo will reach at `level`. */
  cp: number | null;
  /** HP at `level`. */
  hp: number | null;
  /** Stat product at `level`. */
  statProduct: number | null;
  /** Stat product of the OVERALL BEST IV combo for this species + cap. */
  topStatProduct: number | null;
  /** IV combo that produces `topStatProduct`. */
  topIv: { atk: number; def: number; sta: number } | null;
}

export interface SpeciesLeagueRanksInput {
  base: BaseStats;
  iv: { atk: number; def: number; sta: number };
  /** Pokémon level cap for the trainer (50 normal, 51 with Best Buddy). */
  maxLevel?: number;
}

/**
 * Compute Great / Ultra / Master league ranking for one IV combo. The
 * full 4096-combo sweep runs three times (once per league) and takes
 * <10ms in practice; the detail page only renders this on mount so it
 * isn't on a hot path.
 */
export function rankAllLeagues(
  input: SpeciesLeagueRanksInput,
): Record<LeagueId, LeagueRanking> {
  const leagues: LeagueId[] = ["great", "ultra", "master"];
  const out: Partial<Record<LeagueId, LeagueRanking>> = {};
  for (const l of leagues) {
    out[l] = rankOneLeague(l, input);
  }
  // little is computed too but stays optional in the detail UI — most
  // mons can't drop low enough for the 500 cap, so the chip is hidden.
  out.little = rankOneLeague("little", input);
  return out as Record<LeagueId, LeagueRanking>;
}

export function rankOneLeague(
  league: LeagueId,
  input: SpeciesLeagueRanksInput,
): LeagueRanking {
  const cap = LEAGUE_CAPS[league];
  const maxLevel = input.maxLevel ?? 50;
  const myBest = bestStatProductForCombo(input.base, input.iv, cap, maxLevel);
  if (!myBest) {
    return {
      league,
      rank: null,
      percent: null,
      level: null,
      cp: null,
      hp: null,
      statProduct: null,
      topStatProduct: null,
      topIv: null,
    };
  }

  // Sweep every IV combo to find the overall best stat product + rank.
  let topStatProduct = -Infinity;
  let topIv: { atk: number; def: number; sta: number } | null = null;
  const combos: Array<{ atk: number; def: number; sta: number; sp: number }> = [];
  for (let a = 0; a <= 15; a++) {
    for (let d = 0; d <= 15; d++) {
      for (let s = 0; s <= 15; s++) {
        const best = bestStatProductForCombo(
          input.base,
          { atk: a, def: d, sta: s },
          cap,
          maxLevel,
        );
        const sp = best ? best.statProduct : 0;
        combos.push({ atk: a, def: d, sta: s, sp });
        if (sp > topStatProduct) {
          topStatProduct = sp;
          topIv = { atk: a, def: d, sta: s };
        }
      }
    }
  }
  // Rank = strictly-better spreads + 1, so tied spreads share the better
  // rank. This matches leagueFullRank / leagueTopRank and Poké Genie; the
  // old sorted-index lookup handed a tied spread the WORSE position
  // (Eevee 9/9/5 read #1867 where every other surface said #1866).
  let strictlyBetter = 0;
  for (const c of combos) if (c.sp > myBest.statProduct) strictlyBetter++;
  const rank = strictlyBetter + 1;
  const percent = topStatProduct > 0 ? (myBest.statProduct / topStatProduct) * 100 : null;
  return {
    league,
    rank,
    percent,
    level: myBest.level,
    cp: myBest.cp,
    hp: myBest.hp,
    statProduct: myBest.statProduct,
    topStatProduct,
    topIv,
  };
}

/**
 * Compact league label used on detail cards.
 */
export function leagueLabel(l: LeagueId): string {
  switch (l) {
    case "little": return "Little (500)";
    case "great":  return "Great (1500)";
    case "ultra":  return "Ultra (2500)";
    case "master": return "Master";
  }
}

/**
 * 1..4096 rank → percent of pool (e.g. rank 1 → "top 0.0%").
 */
export function rankPercentile(rank: number | null): string {
  if (rank == null) return "-";
  const pct = ((rank - 1) / 4096) * 100;
  if (pct < 0.05) return "top 0.05%";
  if (pct < 1)   return `top ${pct.toFixed(2)}%`;
  if (pct < 10)  return `top ${pct.toFixed(1)}%`;
  return `top ${Math.round(pct)}%`;
}
